import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@env/environment';
import { Diagram, DiagramContent, DiagramRequest } from '../models/diagram.model';

/** Diagramas draw.io de tareas, USs y sugerencias (persistidos en la DB). */
@Injectable({ providedIn: 'root' })
export class GdpDiagramService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.baseApiUrl}/gdp`;

  // ── Tareas ─────────────────────────────────────────────────────────────────

  getTaskDiagrams(taskId: number): Observable<Diagram[]> {
    return this.http.get<Diagram[]>(`${this.apiUrl}/tasks/${taskId}/diagrams`);
  }

  createTaskDiagram(taskId: number, request: DiagramRequest): Observable<Diagram> {
    return this.http.post<Diagram>(`${this.apiUrl}/tasks/${taskId}/diagrams`, request);
  }

  getTaskDiagramContent(diagramId: number): Observable<DiagramContent> {
    return this.http.get<DiagramContent>(`${this.apiUrl}/task-diagrams/${diagramId}`);
  }

  getTaskDiagramPreview(diagramId: number): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/task-diagrams/${diagramId}/preview`, { responseType: 'blob' });
  }

  updateTaskDiagram(diagramId: number, request: DiagramRequest): Observable<Diagram> {
    return this.http.put<Diagram>(`${this.apiUrl}/task-diagrams/${diagramId}`, request);
  }

  deleteTaskDiagram(diagramId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/task-diagrams/${diagramId}`);
  }

  // ── Historias de usuario ───────────────────────────────────────────────────

  getUserStoryDiagrams(userStoryId: number): Observable<Diagram[]> {
    return this.http.get<Diagram[]>(`${this.apiUrl}/user-stories/${userStoryId}/diagrams`);
  }

  createUserStoryDiagram(userStoryId: number, request: DiagramRequest): Observable<Diagram> {
    return this.http.post<Diagram>(`${this.apiUrl}/user-stories/${userStoryId}/diagrams`, request);
  }

  getUserStoryDiagramContent(diagramId: number): Observable<DiagramContent> {
    return this.http.get<DiagramContent>(`${this.apiUrl}/us-diagrams/${diagramId}`);
  }

  getUserStoryDiagramPreview(diagramId: number): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/us-diagrams/${diagramId}/preview`, { responseType: 'blob' });
  }

  updateUserStoryDiagram(diagramId: number, request: DiagramRequest): Observable<Diagram> {
    return this.http.put<Diagram>(`${this.apiUrl}/us-diagrams/${diagramId}`, request);
  }

  deleteUserStoryDiagram(diagramId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/us-diagrams/${diagramId}`);
  }

  // ── Sugerencias ────────────────────────────────────────────────────────────

  getSuggestionDiagrams(suggestionId: number): Observable<Diagram[]> {
    return this.http.get<Diagram[]>(`${this.apiUrl}/suggestions/${suggestionId}/diagrams`);
  }

  createSuggestionDiagram(suggestionId: number, request: DiagramRequest): Observable<Diagram> {
    return this.http.post<Diagram>(`${this.apiUrl}/suggestions/${suggestionId}/diagrams`, request);
  }

  getSuggestionDiagramContent(diagramId: number): Observable<DiagramContent> {
    return this.http.get<DiagramContent>(`${this.apiUrl}/suggestion-diagrams/${diagramId}`);
  }

  getSuggestionDiagramPreview(diagramId: number): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/suggestion-diagrams/${diagramId}/preview`, { responseType: 'blob' });
  }

  updateSuggestionDiagram(diagramId: number, request: DiagramRequest): Observable<Diagram> {
    return this.http.put<Diagram>(`${this.apiUrl}/suggestion-diagrams/${diagramId}`, request);
  }

  deleteSuggestionDiagram(diagramId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/suggestion-diagrams/${diagramId}`);
  }
}
